import * as _feature from '@angular/core'
import { Cache } from '@attain-apps/data-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as ngrxStore from '@ngrx/store'
import * as rxjs from 'rxjs'

import * as _actions from './actions'
import * as _effects from './effects'
import { InjectableDependencies } from './injectable-dependencies'
import { Lifecycle } from './lifecycle'
import type * as models from './models'
import type * as _reducers from './reducers'
import * as _selectors from './selectors'

/**
 * Loosely inspired by
 * {@link https://ngrx.io/guide/store/feature-creators NgRx's version of a feature},
 * this is meant to bundle all the essential aspects of NgRx state management
 * for a particular set of functionality
 *
 * This implementation seeks to solve a few limitations of NgRx's approach:
 * 1. This can be dynamically instantiated at anytime. NgRx's approach requires
 *    the feature to be provided through Angular's dependency injection which
 *    significantly limits the ability to create dynamic, reusable features
 * 2. These {@link Feature} can be logically nested within one another
 * 3. We seek to make everything as self-contained as possible. For example, we
 *    enable dispatching actions, listening for actions, and consuming selectors
 *    as signals or observables all without the needing of additional
 *    dependencies, helper functions, etc. NgRx generally relies on outside
 *    dependencies, such as {@link ngrxStore.Store} and
 *    {@link ngrxStore.Action}, as well as various helper functions to
 *    effectively utilize actions and selectors
 * 4. Building on the last point, this seeks to make common workflows easy,
 *    concise, and robust, particularly as it relates to actions. NgRx tries the
 *    same thing but solves for different workflows than the ones we tend to
 *    encounter in our codebase. For example, we commonly require
 *    request-response based actions, where we make a request and then that
 *    request can succeed, encounter an error, or be cancelled before
 *    completing. We also often chain these requests together in various ways.
 *    We have designed various parts of this library to make this particular
 *    workflow easy and convenient
 */
export class Feature<
  TState,
  TSelectors,
  // NOTE: this must NOT have any default! If it does, it won't get inferred,
  // which breaks a lot
  const TActionsConfig extends _actions.Config.Constraint<NoInfer<TState>>,
> {
  /* --------------------------------- Static --------------------------------- */

  /**
   * Tracks the immediate children of a {@link Feature}
   *
   * Note that this skips any intermediate {@link Feature.Parent}. For example,
   * if you have feature A > parent B > parent C > feature D > feature E, this lookup will look something like:
   * ```ts
   * {
   *   'A': [featureD],
   *   'A/B/C/D': [featureE]
   * }
   * ```
   *
   * Only uses weak references so that unused {@link Feature} are garbage
   * collected
   */
  private static readonly _childrenLookup: Partial<
    Record<string, WeakRef<Feature.Constraint>[]>
  > = {}

  /**
   * Same as {@link extraReducers}, but mutable for internal usage
   */
  private static _extraReducers: _reducers.ExtraReducer.Binding<
    // NOTE: TState needs to be `any` to work with any feature
    _reducers.ExtraReducer<any>
  >[] = []
  /**
   * List of all registered {@link _reducers.ExtraReducer}
   */
  public static readonly extraReducers: readonly _reducers.ExtraReducer.Binding[] =
    this._extraReducers

  /** Used by {@link _getStateSelector} */
  private static readonly _stateSelectorCache = new Cache<
    string,
    _selectors.BoundSelector<Unknown.ButNotNullish>
  >({ maxItems: 1000 })

  /**
   * Registers a child in {@link _childrenLookup}
   */
  private static _addChild(
    parent: Feature.Parent | undefined,
    feature: Feature.Constraint,
  ): void {
    for (; parent && !(parent instanceof Feature); parent = parent?.parent) {}
    if (!parent) {
      return
    }
    const parentId = Feature.Parent.identify(parent).id
    const children = (this._childrenLookup[parentId] ??= [])
    children.push(new WeakRef(feature))
  }

  /**
   * Retrieves a {@link _selectors.BoundSelector} for the full state of a
   * particular {@link Feature}
   *
   * Uses a {@link Cache} so that we can take advantage of NgRx's internal
   * {@link https://en.wikipedia.org/wiki/Memoization memoization}
   */
  private static _getStateSelector(
    feature: Feature.Constraint,
  ): _selectors.BoundSelector<Unknown.ButNotNullish> {
    return this._stateSelectorCache.produce(
      feature.id,
      () =>
        new _selectors.BoundSelector(feature, () =>
          _selectors.Selector.create.usingGlobalState(
            state =>
              (state as Unknown.ButNotNullish)[feature.id] ??
              feature.initialState,
          ),
        ),
    )
  }

  /* -------------------------------- Instance -------------------------------- */

  /** @see {@link InjectableDependencies} */
  public readonly injectables: InjectableDependencies

  /** @see {@link ngrxStore.Store} */
  public get store(): ngrxStore.Store {
    return this.injectables.store
  }

  /**
   * Stream of all actions dispatched from this {@link Feature}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly actions$

  /**
   * Uniquely identifies this {@link Feature}
   *
   * Based on this {@link Feature}'s {@link lineage}
   */
  public readonly id: string

  /**
   * Readable name representing the nature of this {@link Feature}
   */
  public readonly name: string

  /**
   * The immediate {@link Feature.Parent} that this {@link Feature} is nested
   * in, if there is one
   */
  public readonly parent?: Feature.Parent

  /**
   * {@link Feature.Parent.name} for each {@link Feature.Parent} and this
   * {@link Feature}, starting from the root, going down to this {@link Feature}
   *
   * @example
   * [ 'Root', 'Ancestor1', 'Ancestor2', 'Parent', 'CurrentFeature' ]
   */
  public readonly lineage: readonly string[]

  /**
   * All {@link Feature} that are directly nested under this {@link Feature}
   */
  public get children(): readonly Feature.Default[] {
    return (
      Feature._childrenLookup[this.id]
        ?.map(child => child.deref())
        .filter(TypeChecker.isNotNullish.raw) ?? []
    )
  }

  /**
   * Selector for the full state of this {@link Feature}
   *
   * This is usually only used to create the other {@link selectors} of this
   * {@link Feature}
   *
   * @see {@link _selectors.BoundSelector}, {@link Feature._getStateSelector}
   */
  public readonly state: _selectors.BoundSelector<TState>

  /**
   * Contains all of the selectors provided by this {@link Feature}
   *
   * @see {@link _selectors.BoundSelector}
   */
  public get selectors(): TSelectors {
    // Lazily created to avoid issues with selector producer referencing
    // something that does not yet exist, and avoid unnecessary creation of
    // selectors that are not used
    return (this._selectors ??= this._selectorProducer(this))
  }

  /**
   * Contains all of the actions provided by this {@link Feature}
   *
   * Can be used to dispatch or listen to specific actions of this
   * {@link Feature}
   *
   * @see {@link _actions.Group}
   */
  public readonly actions: _actions.Group<TActionsConfig>

  /**
   * Used to manage the dynamic instantiation and destruction of this
   * {@link Feature}
   */
  public readonly lifecycle: Lifecycle<this, TState>

  /** Prevents duplicate lifecycle teardown when Angular and custom code overlap. */
  private _destroyed = false

  /**
   * Used internally to manage the registration of effects
   *
   * @see {@link initEffects}, {@link registerEffects}
   */
  private _effectsInitialized = false
  /**
   * Used internally to manage the registration of effects
   *
   * @see {@link initEffects}, {@link registerEffects}
   */
  // NOTE: Unfortunately because Effect.Registrator is a function if we use a
  // specific type here, it will be contravariant and since the specific type
  // that is supposed to go in here is `this`, that will cause the Feature to be
  // contravariant with itself, which causes a lot of type inference issues
  private readonly _unregisteredEffects: _effects.Effect.Registrator<any>[] = []
  /**
   * Used internally to manage the unregistration of effects
   *
   * @see {@link unregisterAllEffects}
   */
  private readonly _terminateEffects$ = new rxjs.Subject<undefined>()

  /** @see {@link selectors} */
  private readonly _selectorProducer: (
    _this: Feature.Constraint<TState>,
  ) => TSelectors
  /** @see {@link selectors} */
  private _selectors: TSelectors | undefined

  /**
   * Represents the desired initial state of this {@link Feature} before any
   * actions have occurred
   *
   * __NOTE:__ we do not guarantee that this is actually written into the
   * {@link ngrxStore.Store}. In some cases, we simply default to providing this
   * through selectors and reducers when the state for this {@link Feature} in
   * the {@link ngrxStore.Store} is `undefined`. There should be no difference
   * for anything in the codebase, but if looking at the global state through
   * development tools, it may appear that the state for this {@link Feature} is
   * missing or empty, even though we are effectively using this
   * {@link initialState}
   */
  public readonly initialState: Readonly<TState>

  constructor({
    autoInitializeEffects = true,
    name,
    parent,
    initialState,
    selectors,
    actions,
    injectables = parent?.injectables ??
      _feature.inject(InjectableDependencies),
  }: Readonly<Feature.constructor.Params<TState, TSelectors, TActionsConfig>>) {
    this.name = name
    this.parent = parent
    // NOTE: must appear after the name and parent are set
    const { id, lineage } = Feature.Parent.identify(this)
    this.id = id
    this.lineage = lineage
    if (parent) {
      Feature._addChild(parent, this)
    }

    this.injectables = injectables
    this.actions$ = injectables.actions.pipe(
      rxjs.filter(
        (action): action is _actions.Action =>
          _actions.Action.isAction(action) && action.featureId === this.id,
      ),
    )

    this.initialState = initialState

    this.lifecycle = new Lifecycle(this)

    this.actions = _actions.Group.create(this, actions)
    this.state = Feature._getStateSelector(
      this,
    ) as _selectors.BoundSelector<TState>

    try {
      _feature
        .inject(_feature.DestroyRef)
        .onDestroy(() => this.destroy('AngularInjector.Destroyed'))
    } catch {
      // No-op: Feature may be constructed outside an Angular injection context.
    }

    if (autoInitializeEffects) {
      this.initEffects()
    }

    // NOTE: done last since this may use any of the above
    this._selectorProducer = selectors
  }

  /* -------------------------------- Selectors ------------------------------- */

  // TODO: since we only want to use these methods in _selectorProducer, maybe
  // we just provide an argument to that function that looks something like {
  // state, createSelector, createSelectorMethod }. That way those three
  // properties/methods are only used internally

  /**
   * Used to create this {@link Feature}'s {@link selectors}
   *
   * Should generally not be used outside of
   * {@link Feature.constructor.Params.selectors}
   *
   * @example
   * // TODO: add some examples
   */
  public createSelector<TSelection>(
    producer: () => _selectors.Selector<TSelection>,
  ): _selectors.BoundSelector<TSelection> {
    return new _selectors.BoundSelector(this, producer)
  }

  /**
   * Used to create methods on this {@link Feature}'s {@link selectors}
   *
   * Should generally not be used outside of
   * {@link Feature.constructor.Params.selectors}
   *
   * @example
   * // TODO: add some examples
   */
  // NOTE: this is intentionally missing return types in an effort to support
  // generics! Based on this:
  // https://github.com/microsoft/TypeScript/issues/37835#issuecomment-638624108
  public createSelectorMethod<TArgs extends readonly unknown[], TSelection>(
    producer: (
      ...args: TArgs
    ) => _selectors.Selector<TSelection> | _selectors.BoundSelector<TSelection>,
  ) {
    /**
     * Caches the creation of the {@link _selectors.BoundSelector} so that we can
     * take advantage of NgRx's internal
     * {@link https://en.wikipedia.org/wiki/Memoization memoization}
     */
    return Cache.fn((...args: TArgs) => {
      const selector = producer(...args)
      return selector instanceof _selectors.BoundSelector
        ? selector
        : new _selectors.BoundSelector(
            // NOTE: this is unfortunately needed to allow us to avoid having an explict return type per the note above
            this satisfies Feature<
              TState,
              TSelectors,
              TActionsConfig
            > as Feature<TState, TSelectors, TActionsConfig>,
            () => selector,
          )
    })
  }

  /* -------------------------------- Reducers -------------------------------- */

  // TODO: explain how regular reducers work on actions
  /** TODO:  */
  public registerExtraReducers<
    TFeature extends Feature<TState, TSelectors, TActionsConfig>,
  >(
    ...reducers: readonly _reducers.ExtraReducer.Registrator<
      TFeature,
      readonly _actions.Filter<_actions.Action.Constraint>[]
    >[]
  ): void {
    Feature._extraReducers.push(
      ...reducers
        .map(producer =>
          producer(
            // NOTE: we had to introduce `TFeature` to avoid type issues for
            // consumers in certain edge cases, but that introduces a type issue
            // here. So we need to do the casting here.
            this satisfies Feature<
              TState,
              TSelectors,
              TActionsConfig
            > as Feature<TState, TSelectors, TActionsConfig> as TFeature,
          ),
        )
        .filter(TypeChecker.isNotNullish.raw)
        .map(
          (reducer): _reducers.ExtraReducer.Binding<typeof reducer> => ({
            feature: this,
            reducer,
          }),
        ),
    )
  }

  /** TODO:  */
  public unregisterAllExtraReducers({ recursive }: models.Recursive): void {
    // Removes all extra reducers that are from this Feature
    Feature._extraReducers = Feature._extraReducers.filter(
      ({ feature }) => feature !== this,
    )

    // Removes all extra reducers that are children to this Feature
    if (recursive) {
      for (const child of this.children) {
        child.unregisterAllExtraReducers({ recursive })
      }
    }
  }

  /* --------------------------------- Effects -------------------------------- */

  /**
   * Used to register an {@link _effects.Effect.Registrator} (created through
   * {@link _effects.create})
   *
   * __NOTE:__ this {@link _effects.Effect} will start running immediately if
   * this {@link initEffects} has already been called, which happens in the
   * constructor if {@link Feature.constructor.Params.autoInitializeEffects} is
   * not set to `false`. Otherwise, this {@link _effects.Effect} will not start
   * running until {@link initEffects} is called
   */
  public registerEffects(
    ...effectRegistrators: readonly _effects.Effect.Registrator<this>[]
  ): void {
    if (this._destroyed || (this.lifecycle?.isDestroyed ?? false)) {
      return
    }

    if (!this._effectsInitialized) {
      this._unregisteredEffects.push(...effectRegistrators)
      return
    }
    for (const effectRegistrator of effectRegistrators) {
      const effect = effectRegistrator(this)
      if (effect) {
        _effects.submitThroughMetaEffect(
          effect.pipe(rxjs.takeUntil(this._terminateEffects$)),
        )
      }
    }
  }

  /**
   * Termintates all effects registered to this {@link Feature} through
   * {@link registerEffects}
   */
  public unregisterAllEffects({ recursive }: models.Recursive): void {
    this._terminateEffects$.next(undefined)
    this._terminateEffects$.complete()

    // Removes all effects that are children to this Feature
    if (recursive) {
      for (const child of this.children) {
        child.unregisterAllEffects({ recursive })
      }
    }
  }

  /**
   * Starts running effects registered to this {@link Feature} through
   * {@link registerEffects}
   */
  public initEffects(): void {
    this._effectsInitialized = true
    this.registerEffects(...this._unregisteredEffects)
    this._unregisteredEffects.length = 0
  }

  /** Destroys this feature and all child features. */
  public destroy(source: string): void {
    if (this._destroyed || this.lifecycle.isDestroyed) {
      return
    }
    this._destroyed = true

    // Stop this feature's effects before child teardown dispatches any
    // lifecycle actions. The lifecycle reducer repeats this cleanup, but doing
    // it here closes the window where a route-bound feature can react to the
    // next route after its owner has started destroying it.
    this.unregisterAllEffects({ recursive: false })

    for (let idx = this.children.length - 1; idx >= 0; idx--) {
      this.children[idx].destroy(source)
    }

    this.lifecycle.actions.destroy.from(source).dispatch()
  }
}
export namespace Feature {
  export type Constraint<
    TState = any,
    TSelectors = any,
    TActionsConfig extends _actions.Config.Constraint<TState> = any,
  > = Feature<TState, TSelectors, TActionsConfig>
  export type Default<
    TState = unknown,
    TSelectors = unknown,
    TActionsConfig extends
      _actions.Config.Constraint<TState> = _actions.Config.Default,
  > = Feature<TState, TSelectors, TActionsConfig>

  export namespace constructor {
    export interface Params<
      TState,
      TSelectors,
      TActionsConfig extends _actions.Config.Constraint,
    > {
      /**
       * Dependencies that require
       * {@link https://angular.dev/guide/di/dependency-injection Angular injection}.
       *
       * @default
       * ```
       * ```
       * Inheritted from {@link parent}, if available. Otherwise,
       * {@link InjectableDependencies} are injected directly (only possible in
       * an injectable context)
       * @see {@link InjectableDependencies}
       */
      injectables?: InjectableDependencies
      name: string
      parent?: Feature.Parent
      initialState: TState
      selectors: (
        feature: Feature<TState, TSelectors, TActionsConfig>,
      ) => TSelectors
      actions: TActionsConfig
      /**
       * If `false`, effects will not be registered automatically for this
       * {@link Feature}. Instead, {@link Feature.initEffects} must be called
       * when ready to initialize the effects
       *
       * @default true
       */
      autoInitializeEffects?: boolean
    }
  }

  export interface Parent {
    readonly name: string
    readonly parent?: Parent
    readonly injectables?: InjectableDependencies
  }
  export namespace Parent {
    /**
     * Traverses the entire hierarchy of a given {@link Parent} to retrieve the
     * full identifying information for that {@link Parent}
     *
     * @example
     * const root = { name: 'A' }
     * const grandparent = { name: 'B', parent: root }
     * const parent = { name: 'C', parent: grandparent }
     * const child = { name: 'D', parent: parent }
     * const { id, lineage } = Feature.Parent.identify(child)
     * console.log(id) // 'A/B/C/D'
     * console.log(lineage) // [ 'A', 'B', 'C', 'D' ]
     */
    export function identify({ name, parent }: Parent): {
      readonly id: string
      readonly lineage: readonly string[]
    } {
      let id = name
      const lineage: string[] = [name]
      for (
        let currentParent = parent;
        currentParent !== undefined;
        currentParent = currentParent?.parent
      ) {
        if (currentParent instanceof Feature) {
          // If we encounter a Feature, we don't need to keep traversing the
          // hierarchy because the Feature has already done that for us
          return {
            id: `${currentParent.id}/${id}`,
            lineage: [...currentParent.lineage, ...lineage],
          }
        }
        // Otherwise, we add this parent and keep moving up
        id = `${currentParent.name}/${id}`
        lineage.unshift(currentParent.name)
      }
      return { id, lineage }
    }
  }
}
